/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useState} from 'react';

export interface LegacyRefreshCycle {
  isRefreshFetching: boolean;
  updateCycleKey: number;
  onProgressCycleComplete: () => void;
}

/**
 * useLegacyRefreshCycle is the timing driver behind legacy `/tracking`'s
 * "Data Updates Every N Sec" side panel. Unlike tracking2's
 * `useTracking2RefreshCycle`, `onRefreshAssets` IS the one real fetch this
 * cycle owns, so `AssetUpdateProgressBar`'s `onCycleComplete` can await that
 * promise directly - no shared registry needed here.
 *
 * ```typescript
 *
 *   import {useLegacyRefreshCycle} from
 *     '@/routes/tracking/sidesheet/useLegacyRefreshCycle';
 *   ...
 *
 *   const {isRefreshFetching, updateCycleKey, onProgressCycleComplete} =
 *     useLegacyRefreshCycle(onRefreshAssets);
 *
 * ```
 *
 * @param onRefreshAssets        the sheet header's own asset-list refetch
 * @returns isRefreshFetching       `true` while `onRefreshAssets` is in flight
 * @returns updateCycleKey          bumped once per completed cycle - pass to
 *                                  `AssetUpdateProgressBar`'s `cycleKey` to
 *                                  remount (and so restart) its fill
 * @returns onProgressCycleComplete pass to `AssetUpdateProgressBar`'s
 *                                  `onCycleComplete`
 */
export const useLegacyRefreshCycle = (onRefreshAssets: Function): LegacyRefreshCycle => {
  const [isRefreshFetching, setIsRefreshFetching] = useState(false);
  const [updateCycleKey, setUpdateCycleKey] = useState(0);

  const onProgressCycleComplete = useCallback(function() {
    setIsRefreshFetching(true);
    Promise.resolve(onRefreshAssets()).finally(function() {
      setIsRefreshFetching(false);
      setUpdateCycleKey(key => key + 1);
    });
  }, [onRefreshAssets]);

  return {isRefreshFetching, updateCycleKey, onProgressCycleComplete};
};

export default useLegacyRefreshCycle;
